import {
  readUiSettingStorageValue,
  useUiSettingStorageListener,
  writeUiSettingStorageValue,
} from '@affine/core/modules/ui-settings-vault';
import { useCallback, useEffect, useMemo, useState } from 'react';

const SIDEBAR_ORDER_STORAGE_OPTIONS = { sync: 'cloud' } as const;

export type SidebarManualOrderPosition = {
  at: 'before' | 'after';
  key: string;
};

export type SidebarSortMode =
  | 'manual'
  | 'alphabetical'
  | 'folders-first'
  | 'docs-first';

export type SidebarManualOrderItem = {
  key: string;
  label?: string;
  type?: string;
};

export type SidebarManualBase = 'alphabetical' | 'source';

export type SidebarManualOrderOptions = {
  /** Area-owned sort mode. Manual order keys remain local to this list. */
  sortMode?: SidebarSortMode;
  defaultSortMode?: SidebarSortMode;
  manualBase?: SidebarManualBase;
};

function parseOrder(raw: string | null): string[] {
  try {
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((key): key is string => typeof key === 'string')
      : [];
  } catch {
    return [];
  }
}

function readOrder(storageKey: string | null): string[] {
  if (!storageKey) {
    return [];
  }
  return parseOrder(
    readUiSettingStorageValue(storageKey, SIDEBAR_ORDER_STORAGE_OPTIONS)
  );
}

function writeOrder(storageKey: string | null, keys: string[]): void {
  if (!storageKey) {
    return;
  }
  try {
    writeUiSettingStorageValue(
      storageKey,
      JSON.stringify(keys),
      SIDEBAR_ORDER_STORAGE_OPTIONS
    );
  } catch {
    // Order remains in memory for this session.
  }
}

function parseSortMode(
  raw: string | null,
  fallback: SidebarSortMode
): SidebarSortMode {
  try {
    if (
      raw === 'manual' ||
      raw === 'alphabetical' ||
      raw === 'folders-first' ||
      raw === 'docs-first'
    ) {
      return raw;
    }
  } catch {
    return fallback;
  }
  return fallback;
}

function readSortMode(
  storageKey: string | null,
  fallback: SidebarSortMode
): SidebarSortMode {
  if (!storageKey) {
    return fallback;
  }
  return parseSortMode(
    readUiSettingStorageValue(storageKey, SIDEBAR_ORDER_STORAGE_OPTIONS),
    fallback
  );
}

function writeSortMode(storageKey: string | null, mode: SidebarSortMode): void {
  if (!storageKey) {
    return;
  }
  try {
    writeUiSettingStorageValue(storageKey, mode, SIDEBAR_ORDER_STORAGE_OPTIONS);
  } catch {
    // Sort mode remains in memory for this session.
  }
}

function compactOrder(keys: string[]): string[] {
  const seen = new Set<string>();
  return keys.filter(key => {
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function orderItems<T extends SidebarManualOrderItem>(
  items: readonly T[],
  orderKeys: readonly string[]
): T[] {
  const itemByKey = new Map(items.map(item => [item.key, item]));
  const ordered: T[] = [];
  const used = new Set<string>();

  for (const key of orderKeys) {
    const item = itemByKey.get(key);
    if (!item) continue;
    ordered.push(item);
    used.add(key);
  }

  for (const item of items) {
    if (!used.has(item.key)) {
      ordered.push(item);
    }
  }

  return ordered;
}

export function placeSidebarManualOrderItem(
  previousOrderKeys: readonly string[],
  itemKeys: readonly string[],
  itemKey: string,
  position?: SidebarManualOrderPosition
): string[] {
  const visibleKeys = new Set(itemKeys);
  const base = compactOrder([
    ...previousOrderKeys.filter(key => visibleKeys.has(key)),
    ...itemKeys,
  ]);
  if (!base.includes(itemKey)) {
    base.push(itemKey);
  }

  const withoutItem = base.filter(key => key !== itemKey);
  const referenceIndex = position ? withoutItem.indexOf(position.key) : -1;
  const insertIndex =
    referenceIndex === -1
      ? 0
      : position?.at === 'after'
        ? referenceIndex + 1
        : referenceIndex;
  const next = [...withoutItem];
  next.splice(insertIndex, 0, itemKey);
  return next;
}

export function sidebarManualOrderKey(
  type: 'doc' | 'folder',
  id: string
): string {
  return `${type}:${id}`;
}

export function compareSidebarManualOrderItems(
  a: SidebarManualOrderItem,
  b: SidebarManualOrderItem
): number {
  const byLabel = (a.label ?? '').localeCompare(b.label ?? '', undefined, {
    numeric: true,
    sensitivity: 'base',
  });
  return byLabel === 0 ? a.key.localeCompare(b.key) : byLabel;
}

function compareSidebarType(
  a: SidebarManualOrderItem,
  b: SidebarManualOrderItem,
  preferredType: 'folder' | 'doc'
) {
  if (a.type === b.type) {
    return 0;
  }
  if (a.type === preferredType) {
    return -1;
  }
  if (b.type === preferredType) {
    return 1;
  }
  return (a.type ?? '').localeCompare(b.type ?? '');
}

export function sortSidebarItems<T extends SidebarManualOrderItem>(
  items: readonly T[],
  mode: Exclude<SidebarSortMode, 'manual'>,
  compareItems: (a: T, b: T) => number = compareSidebarManualOrderItems
): T[] {
  const sorted = [...items];
  if (mode === 'alphabetical') {
    return sorted.sort(compareItems);
  }
  return sorted.sort((a, b) => {
    const byType =
      mode === 'folders-first'
        ? compareSidebarType(a, b, 'folder')
        : compareSidebarType(a, b, 'doc');
    return byType === 0 ? compareItems(a, b) : byType;
  });
}

export function resolveSidebarOrderedItems<T extends SidebarManualOrderItem>(
  items: readonly T[],
  sortMode: SidebarSortMode,
  orderKeys: readonly string[],
  compareItems: (a: T, b: T) => number = compareSidebarManualOrderItems,
  manualBase: SidebarManualBase = 'alphabetical'
): T[] {
  if (sortMode !== 'manual') {
    return sortSidebarItems(items, sortMode, compareItems);
  }

  const manualBaseItems =
    manualBase === 'source'
      ? [...items]
      : sortSidebarItems(items, 'alphabetical', compareItems);
  return orderItems(manualBaseItems, orderKeys);
}

export function useSidebarSortMode(
  storageKey: string | null,
  defaultMode: SidebarSortMode
) {
  const [sortMode, setSortModeState] = useState<SidebarSortMode>(() =>
    readSortMode(storageKey, defaultMode)
  );

  useEffect(() => {
    setSortModeState(readSortMode(storageKey, defaultMode));
  }, [defaultMode, storageKey]);

  useUiSettingStorageListener(
    storageKey,
    SIDEBAR_ORDER_STORAGE_OPTIONS,
    useCallback(
      rawValue => {
        setSortModeState(parseSortMode(rawValue, defaultMode));
      },
      [defaultMode]
    )
  );

  const setSortMode = useCallback(
    (mode: SidebarSortMode) => {
      writeSortMode(storageKey, mode);
      setSortModeState(mode);
    },
    [storageKey]
  );

  return {
    sortMode,
    setSortMode,
  };
}

export function useSidebarManualOrder<T extends SidebarManualOrderItem>(
  storageKey: string | null,
  items: readonly T[],
  compareItems: (a: T, b: T) => number = compareSidebarManualOrderItems,
  options: SidebarManualOrderOptions = {}
) {
  const {
    sortMode: controlledSortMode,
    defaultSortMode = 'alphabetical',
    manualBase = 'alphabetical',
  } = options;
  const sortModeStorageKey = storageKey ? `${storageKey}:sort-mode` : null;
  const { sortMode: storedSortMode, setSortMode } = useSidebarSortMode(
    sortModeStorageKey,
    defaultSortMode
  );
  const sortMode = controlledSortMode ?? storedSortMode;
  const [orderKeys, setOrderKeys] = useState<string[]>(() =>
    readOrder(storageKey)
  );

  useEffect(() => {
    setOrderKeys(readOrder(storageKey));
  }, [storageKey]);

  useUiSettingStorageListener(
    storageKey,
    SIDEBAR_ORDER_STORAGE_OPTIONS,
    useCallback(rawValue => {
      setOrderKeys(parseOrder(rawValue));
    }, [])
  );

  const orderedItems = useMemo(
    () =>
      resolveSidebarOrderedItems(
        items,
        sortMode,
        orderKeys,
        compareItems,
        manualBase
      ),
    [compareItems, items, manualBase, orderKeys, sortMode]
  );

  const visibleItemKeys = useMemo(
    () => orderedItems.map(item => item.key),
    [orderedItems]
  );

  const placeItem = useCallback(
    (itemKey: string, position?: SidebarManualOrderPosition) => {
      if (controlledSortMode === undefined) {
        setSortMode('manual');
      }
      setOrderKeys(previous => {
        const next = placeSidebarManualOrderItem(
          previous,
          visibleItemKeys,
          itemKey,
          position
        );
        writeOrder(storageKey, next);
        return next;
      });
    },
    [controlledSortMode, setSortMode, storageKey, visibleItemKeys]
  );

  return {
    orderedItems,
    placeItem,
    setSortMode,
    sortMode,
  };
}
